import React, { useEffect, useMemo, useState } from 'react';

import { Web } from '@mui/icons-material';
import dayjs from 'dayjs';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getExportCSVData, getCSVData } from './csv';
import DeductionRegisterTable from './DeductionRegisterTable';
import ExcelBlack from '../../../assets/excel-black.png';
import Excel from '../../../assets/excel.png';
import { Breadcrumbs, Button, EdgeReportingWindow, SubHeader, ReportLoader } from '../../../components';
import { formatIsoToDate, newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';
import getEdgeReportingMenu from '../getEdgeReportingMenu';
import { userHasAccess, generateReport, getReportData, removeReportData, getList } from '../operations';
import DeductionRegisterForm from './DeductionRegisterForm';

import './styles.scoped.css';

const DeductionRegister = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const [data, setData] = useState([]);
  const [dataExport, setDataExport] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({});
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isLoadingDeductions, setIsLoadingDeductions] = useState(false);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);

  const viewFullPii = useMemo(
    () => user?.appAccess?.items?.find(item => item.appId.id === '4')?.roleId?.viewFullPii,
    [user],
  );

  useEffect(() => {
    if (menuItems) {
      // eslint-disable-next-line no-undef
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) navigate('/');
    }
  }, [menuItems]);

  const handleGetDeductions = async () => {
    setIsLoadingDeductions(true);
    const newDeductions = await getList('deduction-codes');
    setDeductions(newDeductions);
    setIsLoadingDeductions(false);
  };
  const handleGetBranches = async () => {
    setIsLoadingBranches(true);
    const newBranches = await getList('clients/branches');
    setBranches(newBranches);
    setIsLoadingBranches(false);
  };

  useEffect(() => {
    handleGetDeductions();
    handleGetBranches();
  }, []);

  const deductionSortOptions = deductions?.map(({ Code, Description }) => ({
    label: Description,
    value: Code,
  }));

  const branchOptions = branches?.map(({ Branch_Code, Branch_Name }) => ({
    label: `${Branch_Name} - ${Branch_Code}`,
    value: Branch_Code,
  }));

  const fileName = `edge-reporting-deduction-register-${newDateFormatted.toString()}.csv`;
  const exportFileName = `edge-reporting-deduction-register-export-${newDateFormatted.toString()}.csv`;

  const handleViewResults = async () => {
    const checkRangeUsed = !!form?.FromCheckdate && !!form?.ToCheckdate;
    const periodRangeUsed = !!form?.FromPerEnddate && !!form?.ToPerEnddate;
    if (checkRangeUsed === periodRangeUsed) {
      // both filled, or neither — the report takes exactly one date range
      // eslint-disable-next-line no-undef
      alert('Please select either a Check Date OR Period Ending Date Range.');
      return;
    }
    if (
      periodRangeUsed &&
      !(
        dayjs(form?.ToPerEnddate).isAfter(form?.FromPerEnddate) ||
        dayjs(form?.FromPerEnddate).isSame(form?.ToPerEnddate)
      )
    ) {
      // eslint-disable-next-line no-undef
      alert('Invalid date range. Please double check and try again.');
      return;
    }
    if (
      checkRangeUsed &&
      !(dayjs(form?.ToCheckdate).isAfter(form?.FromCheckdate) || dayjs(form?.FromCheckdate).isSame(form?.ToCheckdate))
    ) {
      // eslint-disable-next-line no-undef
      alert('Invalid date range. Please double check and try again.');
      return;
    }
    setLoadingResults(true);
    Promise.all([
      generateReport({
        endpoint: 'deduction-register-report',
        payload: {
          FromPerEnddate: form?.FromPerEnddate ? formatIsoToDate(form?.FromPerEnddate, 'YYYY-MM-DD') : '',
          ToPerEnddate: form?.ToPerEnddate ? formatIsoToDate(form?.ToPerEnddate, 'YYYY-MM-DD') : '',
          FromCheckdate: form?.FromCheckdate ? formatIsoToDate(form?.FromCheckdate, 'YYYY-MM-DD') : '',
          ToCheckdate: form?.ToCheckdate ? formatIsoToDate(form?.ToCheckdate, 'YYYY-MM-DD') : '',
          FromEmplLastName: form?.FromEmplLastName ?? '',
          ToEmplLastName: form?.FromEmplLastName ?? '',
          FromEmplFirstName: form?.FromEmplFirstName ?? '',
          ToEmplFirstName: form?.ToEmplFirstName ?? '',
          FromEmplID: form?.FromEmplID ? parseInt(form?.FromEmplID) : '',
          ToEmplID: form?.ToEmplID ? parseInt(form?.ToEmplID) : '',
          SSNO: form?.SSNO ?? '',
          DedCode: form?.DedCode ?? '',
          FromBranch: form?.FromBranch?.toString() ?? '',
          ToBranch: form?.ToBranch?.toString() ?? '',
        },
      }),
      generateReport({
        endpoint: 'deduction-register-export',
        payload: {
          FromPerEnddate: form?.FromPerEnddate ? formatIsoToDate(form?.FromPerEnddate, 'YYYY-MM-DD') : '',
          ToPerEnddate: form?.ToPerEnddate ? formatIsoToDate(form?.ToPerEnddate, 'YYYY-MM-DD') : '',
          FromCheckdate: form?.FromCheckdate ? formatIsoToDate(form?.FromCheckdate, 'YYYY-MM-DD') : '',
          ToCheckdate: form?.ToCheckdate ? formatIsoToDate(form?.ToCheckdate, 'YYYY-MM-DD') : '',
          FromEmplLastName: form?.FromEmplLastName ?? '',
          ToEmplLastName: form?.FromEmplLastName ?? '',
          FromEmplFirstName: form?.FromEmplFirstName ?? '',
          ToEmplFirstName: form?.ToEmplFirstName ?? '',
          FromEmplID: form?.FromEmplID ? parseInt(form?.FromEmplID) : '',
          ToEmplID: form?.ToEmplID ? parseInt(form?.ToEmplID) : '',
          SSNO: form?.SSNO ?? '',
          DedCode: form?.DedCode ?? '',
          FromBranch: form?.FromBranch?.toString() ?? '',
          ToBranch: form?.ToBranch?.toString() ?? '',
          viewFullPii,
        },
      }),
    ])
      .then(values => {
        values.forEach((response, index) => {
          if (response.location) {
            getReportData(response.location).then(async reportData => {
              if (reportData?.length) {
                await removeReportData(response.location);
                if (index === 0) {
                  setData(reportData);
                } else {
                  setDataExport(reportData);
                }
                setShowResults(true);
              }
            });
          } else if (response.statusCode !== 404) {
            if (index === 0) {
              setData(response);
            } else {
              setDataExport(response);
            }
            setShowResults(true);
          } else {
            // eslint-disable-next-line no-undef
            alert('No data found matching your criteria.');
          }
        });
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        setLoadingResults(false);
      });
  };

  return (
    <div style={{ marginBottom: 40 }}>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Deduction Register report?"
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 30,
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'Deduction Register'}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? 'Viewing Report' : 'Deduction Register'}
          route="/edge-reporting"
        />
        {!!data?.length && showResults && (
          <div className="excel-container">
            {!!dataExport?.length && (
              <div>
                <CSVLink filename={exportFileName} data={getExportCSVData(dataExport)}>
                  <ImageColorChanger
                    src={ExcelBlack}
                    brandColor="#000000"
                    style={{ color: 'black', padding: '20px 30px 0 0 ', cursor: 'pointer' }}
                    alt="export-to-excel"
                  />
                </CSVLink>
                <p style={{ fontSize: 12 }}>Garn.</p>
              </div>
            )}
            <CSVLink filename={fileName} data={getCSVData(data, t)}>
              <ImageColorChanger
                src={Excel}
                style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
                alt="export-to-excel"
              />
            </CSVLink>
          </div>
        )}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          <DeductionRegisterTable data={data} loading={loadingResults} />
          <div className="buttons-row">
            <Button variant="primary" onPres={() => setShowResults(false)} style={{ marginRight: '20px', width: '100px' }} text="Back" />

            <Button
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{
            border: '2px solid #343a40',
          }}
          windowContent={
            <DeductionRegisterForm
              form={form}
              setForm={setForm}
              isLoadingDeductions={isLoadingDeductions}
              deductionSortOptions={deductionSortOptions}
              isLoadingBranches={isLoadingBranches}
              branchOptions={branchOptions}
              handleViewResults={handleViewResults}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </div>
  );
};

export default DeductionRegister;
